import { type Money } from "./application";

export enum PropertyKind {
  MultiFamily = "multi_family",
  SingleFamily = "single_family"
}

export enum MediaAttachmentResolution {
  Full = "full",
  Small = "small"
}
interface MediaAttachment {
  global_id: string;
  url: string;
  content_type: string | null;
  original_filename: string | null;
  checksum: string | null;
  extension: string;
  size: number;
  resolution: MediaAttachmentResolution;
}

export interface MediaItem {
  id: number;
  position: number;
  is_cover: boolean;
  created_at: string;
  updated_at: string | null;
  attachments?: MediaAttachment[];
}

export interface ListingPlan {
  beds: number;
  sqft: number;
  baths: number;
}

export interface ImageVariantUrl {
  full: string;
  small: string;
}

interface Amenity {
  key: string;
  level: "property" | "unit";
  title: string;
}

export interface PartnerDetails {
  apply_token: string;
  amenities: {
    property: Amenity[];
    unit: Amenity[];
  };
  name: string;
  slug: string;
  url: string;
  asset_logo_url: string | null;
  colors?: {
    primary: Record<string, string>;
    background?: Record<string, string>;
  };
  phone_number: string | null;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone_number: string;
  email: string;
  is_english_only?: boolean;
}

export interface PropertyDetails {
  name: string;
  slug: string;
  primary_image_url: ImageVariantUrl | null;
  kind: PropertyKind;
  address: {
    address1: string;
    one_line_address: string;
    city: string;
    state_or_province: string;
    postal_code: string;
    country: string;
  };
  branding: {
    enabled?: boolean;
    landlord_name?: string;
    landlord_phone?: string;
    landlord_email?: string;
    logo?: {
      value: string;
      asset: {
        url: string;
        content_type: string;
        filename: string;
        size: number;
        extension: string;
      };
    };
    palette?: Record<string, string>;
  };
}

export type RentDisplayMode = "both" | "home" | "site" | "none";

export type ListingPricePrimaryDisplay = "sale_price" | "home_rent" | "hide";
export type ListingPriceSecondaryDisplay = "site_rent" | "hide";

export interface MapPin {
  slug: string;
  lat: number;
  lng: number;
  unit_url: string;
  title: string;
  primary_image_url: ImageVariantUrl | null;
  gallery_urls: ImageVariantUrl[];
  monthly_rent_amount: Money | null;
  home_rent_amount: Money | null;
  site_rent_amount: Money | null;
  total_rent_amount: Money | null;
  rent_display_mode: RentDisplayMode;
  listing_price_primary: Money | null;
  listing_price_primary_display: ListingPricePrimaryDisplay | null;
  listing_price_secondary: Money | null;
  listing_price_secondary_display: ListingPriceSecondaryDisplay | null;
  sale_price: Money | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  occupied: boolean;
  cats_allowed: boolean;
  dogs_allowed: boolean;
  property_name: string;
  property_address: string;
}

export type SecurityDepositKind = "first_month_rent" | "amount";

export interface ListingAdditionalFees {
  application_fee: {
    enabled: boolean;
    amount: Money | null;
  };
  security_deposit: {
    enabled: boolean;
    kind: SecurityDepositKind | null;
    amount: Money | null;
  };
  other_fees: {
    enabled: boolean;
    text: string | null;
  };
}

interface BaseListing {
  id: number;
  slug: string;
  partner_slug: string;
  title: string;
  description: string | null;
  primary_image_url: ImageVariantUrl | null;
  gallery_urls: ImageVariantUrl[];
  monthly_rent_amount: Money | null;
  home_rent_amount: Money | null;
  site_rent_amount: Money | null;
  total_rent_amount: Money | null;
  rent_display_mode: RentDisplayMode;
  listing_price_primary: Money | null;
  listing_price_secondary: Money | null;
  listing_price_primary_display: ListingPricePrimaryDisplay | null;
  listing_price_secondary_display: ListingPriceSecondaryDisplay | null;
  sale_price: Money | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  available_at: string | null;
  occupied: boolean;
  cats_allowed: boolean;
  dogs_allowed: boolean;
  city: string;
  state_or_province: string;
  postal_code: string;
  unit_url: string;
  lat: number | null;
  lng: number | null;
  media: MediaItem[];
  property: PropertyDetails;
  amenities: {
    id: number;
    title: string;
  }[];
  show_apply_now_button: boolean;
  additional_fees: ListingAdditionalFees | null;
}

interface WithML {
  can_apply: true;
  magic_link: {
    id: string;
    monthly_rent_amount: Money;
    created_at: string;
    updated_at: string;
    link: string;
    active: boolean;
    name: string | null;
    email: string | null;
    unit_id: string;
    property_id: string;
    application_id: string | null;
    kind: "unit";
  };
}

interface WithoutML {
  can_apply: false;
}

export type Listing = BaseListing & (WithML | WithoutML);

export interface PrescreeningQuestion {
  id: string;
  type: "yes_no";
  name: string;
  question: string;
  required: boolean;
  order: number;
}

export enum PrescreeningMode {
  Range = "ranges",
  Numeric = "numeric"
}
export interface ListingButtons {
  show_apply_now_button: boolean;
  show_request_showing_button: boolean;
  show_contact_us_button: boolean;
}
export interface PrescreeningQuestionRecord {
  id: number;
  prescreening_template_identifier: string;
  created_at: string;
  updated_at: string;
}
export type PrescreeningCreditScore = PrescreeningQuestionRecord & {
  score: number | null;
  score_range: string | null;
};
export type PrescreeningGrossMonthlyIncome = PrescreeningQuestionRecord & {
  income_cents: number | null;
  income_range: string | null;
};
